import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Catcher } from '../';
import { history } from '../../Pages/history';
import { IGetApiType } from '../../REST';
import { AppSystem, IShowFormComponent, NotificationType } from '../../interfaces';
import { useAppSystem, useCompanyInfo, useCustomerPrivacyCheck, useProfile, useRouterStore } from '../../hooks';
import {
    abortSelectedFetch,
    addNotification,
    cleanCompanyRelationSelect,
    cleanCompanyStore,
    getCompanyById,
    getCompanyRelationById,
    getCompanyRelationSelect,
    getCompanyRelationSelectById,
} from '../../middleware';
import { tI, tNM } from '../../translate';
import { EditCompanyForm, ShowCompany } from './Components';

export interface ICompanyProps extends IShowFormComponent {
    isMe?: boolean;
}

export const Company: React.FC<ICompanyProps> = observer(({ isMe, isEdit, isNew }: ICompanyProps) => {
    const routerStore = useRouterStore();
    const [id, setId] = useState('');
    const { customerPrivacy = true } = useCompanyInfo();
    const isAvailable = useCustomerPrivacyCheck(customerPrivacy);
    const myCompanyId = useProfile().companyId || '';
    const appSystem = useAppSystem();
    const isBillingSystem = appSystem === AppSystem.BILLING;

    const usedApiTypes: IGetApiType[] = [
        'GET_COMPANIES_SELECT',
        'GET_COMPANY',
        'GET_COMPANY_CONFIG',
        'GET_COMPANY_CONFIG_MFA_SELECT',
        'GET_COMPANY_TYPES_SELECT',
        'GET_COUNTRIES_LIST',
        'GET_COUNTRIES_SELECT',
        'GET_CURRENCY_SELECT',
        'GET_DATA_UNIT_TYPE_SELECT',
        'GET_FEATURE_FLAG_RULES',
        'GET_PAYMENT_SELECT',
        'GET_SALES_RATES_SELECT',
        'GET_USERS_BY_COMPANY_SELECT',
        'GET_WHO_AM_I',
    ];

    const cleanStoreOnGoOut = (): void => {
        abortSelectedFetch(usedApiTypes);
        cleanCompanyRelationSelect();
        cleanCompanyStore();
    };

    const goToList = (): void => {
        cleanStoreOnGoOut();
        routerStore.goTo(isBillingSystem ? 'BillingCompaniesList' : 'CompaniesList');
    };

    const _getCompanyById = async (id: string, _isEdit = false): Promise<void> => {
        const isGetCompany = await getCompanyById(id);
        getCompanyRelationById(id);
        if (!isGetCompany) {
            addNotification({
                type: NotificationType.ERROR,
                message: tNM('Not Found'),
                description: `${tI('Company id')} - "${id}" ${tI('not found')}`,
                duration: 5,
                isTranslated: true,
                isUsersError: true,
            });
            goToList();
        } else if (_isEdit) {
            getCompanyRelationSelect();
            getCompanyRelationSelectById(id);
        }
    };

    const setEdit = (isEdit?: boolean): void => {
        if (isAvailable && !isNew && isEdit) {
            addNotification({
                type: NotificationType.WARNING,
                message: 'Not authorized!',
                description: `Your company has not been granted permission to view this company information`,
                duration: 5,
            });
        } else if (isNew) {
            routerStore.goBack();
        } else if (isEdit) {
            if (isMe || id === myCompanyId) {
                routerStore.goTo('MyCompanyEdit');
            } else {
                routerStore.goTo(isBillingSystem ? 'BillingCompanyEdit' : 'CompanyEdit', { params: { id } });
            }
        } else {
            routerStore.goBack();
        }
    };

    useEffect(() => {
        return (): void => {
            cleanStoreOnGoOut();
        };
    }, []);

    useEffect(() => {
        if (id) {
            _getCompanyById(id, isEdit);
        } else if (!isEdit && isNew) {
            getCompanyRelationSelect();
        }
    }, [id, isEdit, isNew]);

    useEffect(() => {
        if (
            routerStore.routerState.routeName === 'MyCompany' ||
            routerStore.routerState.routeName === 'MyCompanyEdit'
        ) {
            setId(myCompanyId);
        } else if (
            routerStore.routerState.params.id &&
            (routerStore.routerState.routeName === 'Company' ||
                routerStore.routerState.routeName === 'CompanyEdit' ||
                routerStore.routerState.routeName === 'BillingCompany' ||
                routerStore.routerState.routeName === 'BillingCompanyEdit')
        ) {
            setId(routerStore.routerState.params.id);
        }
    }, [routerStore.routerState.routeName, routerStore.routerState.params.id]);

    useEffect(() => {
        if (id && id === myCompanyId) {
            if (isEdit) {
                routerStore.replace({ name: 'MyCompanyEdit', params: {}, queryParams: {} });
                history.replace('/my_company/edit');
            } else {
                routerStore.replace({ name: 'MyCompany', params: {}, queryParams: {} });
                history.replace('/my_company');
            }
        }
    }, [id]);

    return (
        <Catcher>
            {isEdit || isNew ? (
                <EditCompanyForm setEditMode={setEdit} isNew={isNew} />
            ) : (
                <ShowCompany setEditMode={setEdit} />
            )}
        </Catcher>
    );
});
